/**
 * PointsStatusCard 的 KV 文案 + 主题色 + 状态计算 + 数值格式化。
 *
 * 文案走 KV（POINTS_STATUS_CONFIG_KEY），运营可改不发版；KV 未配置 / 解析失败时
 * 回退 DEFAULT_POINTS_STATUS_COPY（按设计稿）。颜色按设计稿硬编码（设计未用变量）。
 */
import CONFIG from '../../../constants/config'
import type { PointsStatusCopy, PointsThemeKey, UsageState } from './types'

/** KV key（需注册进 CONFIG.KVParams.names 才会随启动拉取到 Redux） */
export const POINTS_STATUS_CONFIG_KEY = 'web_points_status_card'

export interface AiPhotoKingPromotion {
  percent: number
  endAt: Date
}

export function parseAiPhotoKingPromotion(
  raw: unknown
): AiPhotoKingPromotion | null {
  try {
    const config = typeof raw === 'string' ? JSON.parse(raw) : raw
    const promotion = config?.ai_photo_king_promotion
    const percent = Number(promotion?.promotion_percent)
    const endAt = new Date(promotion?.promotion_end_at)
    if (
      !Number.isFinite(percent) ||
      percent <= 0 ||
      Number.isNaN(endAt.getTime())
    ) {
      return null
    }
    return { percent, endAt }
  } catch {
    return null
  }
}

/** 默认文案（KV 未配置 / 解析失败时兜底，逐字对齐设计稿） */
export const DEFAULT_POINTS_STATUS_COPY: PointsStatusCopy = {
  kinds: {
    none: {
      tag: '免费试用',
      sectionMain: '试用积分',
      normalHint: '非会员一次性赠送 100 万 试用积分',
      alertHint: '试用即将结束 · 升级会员每月送 300 万 起',
      cta: '升级会员',
      chipUnused: '100 万 积分',
      monthly: false
    },
    basic: {
      tag: '基础会员',
      sectionMain: '本月可用积分',
      normalHint: '基础会员每月 300 万 积分 · 每月 1 号自动刷新',
      alertHint: '本月配额即将用完 · 升级会员每月 600 万 起',
      cta: '升级会员',
      chipUnused: '300 万 / 月 积分',
      monthly: true
    },
    advanced: {
      tag: '高级会员',
      sectionMain: '本月可用积分',
      normalHint: '高级会员每月 600 万 积分 · 每月 1 号自动刷新',
      alertHint: '本月配额即将用完 · 升级超级会员每月 3000 万',
      cta: '升级会员',
      addonCta: '增购积分包',
      chipUnused: '600 万 / 月 积分',
      monthly: true
    },
    super: {
      tag: '超级会员',
      sectionMain: '本月可用积分',
      normalHint: '超级会员每月 3000 万 积分 · 每月 1 号自动刷新',
      alertHint: '本月配额即将用完 · 可增购积分包永久有效',
      cta: '增购积分包',
      chipUnused: '3000 万 / 月 积分',
      monthly: true
    }
  },
  states: {
    unused: { sectionSub: '总额度' },
    normal: { sectionSub: '剩余' },
    warning: { sectionSub: '即将耗尽' },
    critical: { sectionSub: '已耗尽' }
  }
}

/** 身份主题色（按设计稿精确值） */
export interface PointsTheme {
  /** 主色：数值 / 进度 / CTA / 联系客服 / chip 字 */
  main: string
  /** 深色：常规提示横幅文字（非会员用比主色更深的蓝，其余等于主色） */
  deep: string
  /** 浅底：积分区 / 提示区 / chip 底 */
  soft: string
  /** 身份标签底 */
  tagBg: string
  /** 身份标签字 */
  tagColor: string
}

export const POINTS_THEMES: Record<PointsThemeKey, PointsTheme> = {
  none: {
    main: '#297af5',
    deep: '#145cd9',
    soft: '#f2f7ff',
    tagBg: '#f2f7ff',
    tagColor: '#145cd9'
  },
  basic: {
    main: '#fa8c33',
    deep: '#fa8c33',
    soft: '#fff5e0',
    tagBg: '#fff5e0',
    tagColor: '#fa8c33'
  },
  advanced: {
    main: '#f76a07',
    deep: '#f76a07',
    soft: '#fef3ea',
    tagBg: '#ffe9d9',
    tagColor: '#f76a07'
  },
  super: {
    main: '#6b45c2',
    deep: '#6b45c2',
    soft: '#f7f3ff',
    tagBg: '#6b45c2',
    tagColor: '#ffffff'
  }
}

/** 预警 / 危急状态覆盖色（盖掉身份主题色，作用于数值 / 进度 / chip） */
export interface PointsStateStyle {
  accent: string
  soft: string
  pbTrack: string
}

export const POINTS_STATE_STYLES: Record<
  'warning' | 'critical',
  PointsStateStyle
> = {
  warning: { accent: '#fa8c33', soft: '#fff5e0', pbTrack: '#ffffff' },
  critical: { accent: '#ed4545', soft: '#ffeded', pbTrack: '#e0e8f0' }
}

// 预警 / 危急的提示区固定橙色系（与数值的红色区分开）
export const ALERT_HINT_BG = '#fff5e0'
export const ALERT_HINT_COLOR = '#db6b0d'
// 预警 / 危急的 CTA / 联系客服固定橙色
export const ALERT_ACTION_COLOR = '#fa8c33'

/** CONFIG.VIP_KIND → 主题 key（企业级 EVIP 暂归超级，矩阵无独立态） */
export function vipKindToThemeKey(vipKind?: number): PointsThemeKey {
  switch (vipKind) {
    case CONFIG.VIP_KIND.VIP:
      return 'basic'
    case CONFIG.VIP_KIND.VVIP:
      return 'advanced'
    case CONFIG.VIP_KIND.SVIP:
    case CONFIG.VIP_KIND.EVIP:
      return 'super'
    default:
      return 'none'
  }
}

/** 当前会员档位 → 升级目标档位 vipKind（积分 CTA 打开会员弹窗时默认定位「升一档」，
 *  而不是永远落在基础会员）。
 *  等级排序：非会员 < VIP(基础) < VVIP(高级) < SVIP(超级) < EVIP(企业)；
 *  注意 vip_kind 数值不代表等级高低（VIP=1，SVIP=2，VVIP=3，EVIP=4）。
 *  超级/企业会员的 CTA 是「增购积分」（走积分加量包，不到这里），所以实际入参只会是
 *  非会员 / 基础 / 高级三种，兜底 case 仅为健壮性。 */
export function getUpgradeTargetVipKind(currentVipKind: number): number {
  switch (currentVipKind) {
    case CONFIG.VIP_KIND.VIP: // 基础 → 高级
      return CONFIG.VIP_KIND.VVIP
    case CONFIG.VIP_KIND.VVIP: // 高级 → 超级
      return CONFIG.VIP_KIND.SVIP
    case CONFIG.VIP_KIND.SVIP: // 超级 → 企业（兜底，正常走增购到不了这）
    case CONFIG.VIP_KIND.EVIP: // 企业（已最高，兜底）
      return CONFIG.VIP_KIND.EVIP
    default:
      // 非会员 → 基础
      return CONFIG.VIP_KIND.VIP
  }
}

/** 默认单次查询消耗（判断「不足 1 次」的阈值，可被 props.perQueryCost 覆盖）。
 *  剩余 < 此值即「不足 1 次」，走 critical 红色态。2026-06 产品定为 1.5 万。 */
export const DEFAULT_PER_QUERY_COST = 15000

/** 由用量算出使用状态 */
export function computeUsageState(
  total: number,
  used: number,
  perQueryCost = DEFAULT_PER_QUERY_COST
): UsageState {
  if (!total || total <= 0) return 'unused'
  // 没用过 —— 满额（优先判，避免小额度时 used=0 被误判为危急）
  if (used <= 0) return 'unused'
  const remaining = Math.max(0, total - used)
  // 剩余不够一次查询 —— 危急
  if (remaining < perQueryCost) return 'critical'
  // 已用 ≥ 90% —— 预警
  if (used / total >= 0.9) return 'warning'
  return 'normal'
}

/** 数值格式化：原始千分位（大数值展示用，如 1,000,000） */
export function formatRaw(n: number): string {
  return Math.max(0, Math.round(n)).toLocaleString('en-US')
}

/** 去掉小数末尾的 0（58.54 / 100 / 0.5） */
function trimZero(n: number): string {
  return parseFloat(n.toFixed(2)).toString()
}

/** 数值格式化：万 / 亿（说明 / chip 用，如 100 万 / 1 亿；不足 1 万走千分位） */
export function formatWan(n: number): string {
  const v = Math.max(0, n)
  if (v >= 1e8) return `${trimZero(v / 1e8)} 亿`
  if (v >= 1e4) return `${trimZero(v / 1e4)} 万`
  return v.toLocaleString('en-US')
}

/** 解析 KV 配置；逐 kind / state 深合并，缺字段用默认兜底 */
export function parsePointsStatusCopy(raw: unknown): PointsStatusCopy {
  if (!raw) return DEFAULT_POINTS_STATUS_COPY
  try {
    const obj = typeof raw === 'string' ? JSON.parse(raw) : raw
    if (!obj || typeof obj !== 'object') return DEFAULT_POINTS_STATUS_COPY
    const src = obj as Partial<PointsStatusCopy>

    const kinds = { ...DEFAULT_POINTS_STATUS_COPY.kinds }
    if (src.kinds) {
      ;(Object.keys(kinds) as PointsThemeKey[]).forEach((k) => {
        if (src.kinds && src.kinds[k]) {
          kinds[k] = { ...kinds[k], ...src.kinds[k] }
        }
      })
    }

    const states = { ...DEFAULT_POINTS_STATUS_COPY.states }
    if (src.states) {
      ;(Object.keys(states) as UsageState[]).forEach((s) => {
        if (src.states && src.states[s]) {
          states[s] = { ...states[s], ...src.states[s] }
        }
      })
    }

    return { kinds, states }
  } catch {
    return DEFAULT_POINTS_STATUS_COPY
  }
}
